import { UseMutateFunction } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { User } from '@/features/auth/types/user';
import { GetProjectInvitationResult } from '@/features/projects/project-invitation/api/get-project-invitation';
import {
  manageProjectInvitation,
  ManageProjectInvitationInput,
} from '@/features/projects/project-invitation/api/manage-project-invitation';
import { useGetProjectInvitation } from '@/features/projects/project-invitation/hooks/use-get-project-invitation';
import { useManageProjectInvitationMutation } from '@/features/projects/project-invitation/hooks/use-manage-project-invitation-mutation';
import {
  FlowLayout,
  FlowLayoutBody,
} from '@/shared/components/flow/flow-layout';
import { Background } from '@/shared/components/layouts/background';
import { LogoWithName } from '@/shared/components/theme/logo-with-name';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Spinner } from '@/shared/components/ui/spinner';
import { getInitials } from '@/shared/utils/avatar-util';

interface InvitationError {
  title: string;
  message: string;
}

interface InvitationCardProps {
  title: string;
  children: React.ReactNode;
  isError?: boolean;
}

const InvitationCard = ({
  title,
  children,
  isError = false,
}: InvitationCardProps) => {
  return (
    <Card variant={'styled'}>
      <CardHeader className="bg-muted/20">
        <CardTitle
          className={`text-center ${isError ? 'text-destructive' : ''}`}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 pt-6">
        {children}
      </CardContent>
    </Card>
  );
};

const ErrorCard = ({ title, message }: InvitationError) => {
  const navigate = useNavigate();
  const { t } = useTranslation('project', { keyPrefix: 'projectInvitation' });

  return (
    <InvitationCard title={title} isError>
      <p className="text-center text-destructive">{message}</p>
      <Button onClick={() => navigate(paths.home.getHref())} variant="outline">
        {t('goHome')}
      </Button>
    </InvitationCard>
  );
};

const LoadingCard = () => {
  const { t } = useTranslation('project', { keyPrefix: 'projectInvitation' });

  return (
    <InvitationCard title={t('processingTitle')}>
      <Spinner size="lg" />
      <p className="text-center text-muted-foreground">
        {t('processingMessage')}
      </p>
    </InvitationCard>
  );
};

const InvitationActions = ({
  invitation,
  user,
  invitationToken,
  mutate,
}: {
  invitation: GetProjectInvitationResult;
  user: User;
  invitationToken: string;
  mutate: UseMutateFunction<
    Awaited<ReturnType<typeof manageProjectInvitation>>,
    unknown,
    ManageProjectInvitationInput
  >;
}) => {
  const { t } = useTranslation('project', { keyPrefix: 'projectInvitation' });

  const handleAccept = () => mutate({ invitationToken, status: 'accepted' });
  const handleDecline = () => mutate({ invitationToken, status: 'declined' });

  return (
    <Card variant={'styled'} wrapWithStars>
      <CardHeader className="items-center space-y-14 pt-14">
        {invitation.project && (
          <Avatar className="size-24">
            <AvatarFallback>
              {getInitials(invitation.project?.name || '')}
            </AvatarFallback>
          </Avatar>
        )}

        <CardTitle className="text-4xl font-medium">{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <p className="text-center text-xl">
          <Trans
            ns="project"
            i18nKey="projectInvitation.description"
            values={{
              user_name: user.user_metadata.name,
              project_name: invitation.project?.name,
            }}
          />
        </p>
        <p className="text-center text-xl">{t('prompt')}</p>
      </CardContent>
      <CardFooter className="mt-14 justify-center gap-12 pb-14">
        <Button onClick={handleDecline} variant="outline" btnType="primary">
          {t('declineButton')}
        </Button>
        <Button
          onClick={handleAccept}
          variant="greenGradient"
          btnType="primary"
        >
          {t('acceptButton')}
        </Button>
      </CardFooter>
    </Card>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <Background>
      <div className="fixed left-10 top-10">
        <LogoWithName />
      </div>
      <FlowLayout>
        <FlowLayoutBody>{children}</FlowLayoutBody>
      </FlowLayout>
    </Background>
  );
};

export const ManageProjectInvitation = () => {
  const { t } = useTranslation('project', { keyPrefix: 'projectInvitation' });
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitationToken');

  const { data: user, isLoading: isUserLoading } = useUser();
  const {
    data: invitation,
    isLoading: isInvitationLoading,
    error: invitationError,
  } = useGetProjectInvitation({
    invitationToken: invitationToken || '',
    enabled: !!invitationToken && !!user,
  });

  if (!invitationToken) {
    return <ErrorCard title={t('errorTitle')} message={t('missingToken')} />;
  }

  return (
    <Layout>
      <Body
        isUserLoading={isUserLoading}
        isInvitationLoading={isInvitationLoading}
        invitation={invitation}
        invitationError={invitationError}
        user={user}
        invitationToken={invitationToken}
      />
    </Layout>
  );
};

const Body = ({
  isUserLoading,
  isInvitationLoading,
  invitation,
  invitationError,
  user,
  invitationToken,
}: {
  isUserLoading: boolean;
  isInvitationLoading: boolean;
  invitation: GetProjectInvitationResult | undefined;
  invitationError: Error | null | undefined;
  user: User | null | undefined;
  invitationToken: string;
}) => {
  const { t } = useTranslation('project', { keyPrefix: 'projectInvitation' });
  const navigate = useNavigate();

  const { mutate, isPending } = useManageProjectInvitationMutation({
    onSuccess: () => {
      const projectId = invitation?.project?.id;
      if (invitation?.status === 'accepted' && projectId) {
        navigate(paths.projects.details.base.getHref(projectId.toString()));
      } else {
        navigate(paths.home.getHref());
      }
    },
  });

  if (isUserLoading || isInvitationLoading || isPending) {
    return <LoadingCard />;
  }

  if (invitationError && !invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('invalidToken')} />;
  }

  if (!invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('fetchError')} />;
  }

  // Check if the logged-in user is the intended recipient
  if (user?.id !== invitation.org_member.user_id) {
    return <ErrorCard title={t('errorTitle')} message={t('wrongUser')} />;
  }

  return (
    <InvitationActions
      invitationToken={invitationToken}
      invitation={invitation}
      user={user}
      mutate={mutate}
    />
  );
};
